import React, { useState, useEffect } from 'react';
import {
  Box,
  FormHelperText,
  Typography,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  InputBase,
  IconButton,
  Divider,
  styled,
  alpha
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Location } from './api';
import { getCachedLocations } from './locationsCache';

interface LocationSelectorProps {
  value?: string;  // Changed to accept undefined
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

// Define the hierarchical structure
interface LocationHierarchy {
  states: Map<string, {
    pk: string;
    cities: Map<string, {
      pk: string;
      neighborhoods: Map<string, {
        pk: string;
      }>;
    }>;
  }>;
}

// Parse a location key
const parseLocationKey = (key: string): { state?: string; city?: string; neighborhood?: string } => {
  if (!key) return {};
  
  const parts = key.split('#');
  
  // Handle standard format: LOCATION#STATE#CITY#NEIGHBORHOOD
  if (parts[0] === 'LOCATION') {
    return {
      state: parts.length > 1 ? parts[1] : undefined,
      city: parts.length > 2 ? parts[2] : undefined,
      neighborhood: parts.length > 3 ? parts[3] : undefined,
    };
  }
  
  // Handle shortened format: STATE#CITY#NEIGHBORHOOD (without LOCATION prefix)
  return {
    state: parts.length > 0 ? parts[0] : undefined,
    city: parts.length > 1 ? parts[1] : undefined,
    neighborhood: parts.length > 2 ? parts[2] : undefined,
  };
};

// Styled components for the search box
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginBottom: theme.spacing(1),
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: '100%',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 1),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(3)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
  },
}));

// Styled list container
const ScrollableList = styled(Paper)(({ theme }) => ({
  height: 300,
  overflow: 'auto',
  flex: 1,
  boxShadow: theme.shadows[1],
  borderRadius: theme.shape.borderRadius,
}));

// Styled list item for selected items
const SelectedListItem = styled(ListItemButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
  },
}));

const LocationSelector: React.FC<LocationSelectorProps> = ({
  value,
  onChange,
  error,
  disabled,
}) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [hierarchy, setHierarchy] = useState<LocationHierarchy>({ states: new Map() });
  const [loading, setLoading] = useState(true);
  
  // Selected values
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<string>('');

  // Search filters
  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [neighborhoodFilter, setNeighborhoodFilter] = useState('');

  // Build hierarchy from locations
  const buildHierarchy = (locs: Location[]): LocationHierarchy => {
    const result: LocationHierarchy = { states: new Map() };
    
    locs.forEach(loc => {
      const { PK } = loc;
      const parts = parseLocationKey(PK);
      
      if (!parts.state) return;
      
      // Ensure the state exists in our hierarchy
      if (!result.states.has(parts.state)) {
        result.states.set(parts.state, {
          pk: `LOCATION#${parts.state}`,
          cities: new Map(),
        });
      }
      
      // If this is just a state-level location, we're done
      if (!parts.city) return;
      
      // Ensure the city exists in our hierarchy
      const stateData = result.states.get(parts.state)!;
      if (!stateData.cities.has(parts.city)) {
        stateData.cities.set(parts.city, {
          pk: `LOCATION#${parts.state}#${parts.city}`,
          neighborhoods: new Map(),
        });
      }
      
      // If this is just a city-level location, we're done
      if (!parts.neighborhood) return;
      
      // Add the neighborhood
      const cityData = stateData.cities.get(parts.city)!;
      cityData.neighborhoods.set(parts.neighborhood, {
        pk: PK, // Use the exact PK from the data
      });
    });
    
    return result;
  };

  // Fetch locations with caching
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const fetchedLocations = await getCachedLocations();
        setLocations(fetchedLocations);
        
        const builtHierarchy = buildHierarchy(fetchedLocations);
        setHierarchy(builtHierarchy);
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLocations();
  }, []);

  // Initialize with the provided value
  useEffect(() => {
    if (!value || value === '') {
      setSelectedState('');
      setSelectedCity('');
      setSelectedNeighborhood('');
      return;
    }
    
    if (hierarchy.states.size > 0) {
      const parts = parseLocationKey(value);
      
      if (parts.state) {
        setSelectedState(parts.state);
        
        if (parts.city) {
          setSelectedCity(parts.city);
          
          if (parts.neighborhood) {
            setSelectedNeighborhood(parts.neighborhood);
          } else {
            setSelectedNeighborhood('');
          }
        } else {
          setSelectedCity('');
          setSelectedNeighborhood('');
        }
      } else {
        setSelectedState('');
        setSelectedCity('');
        setSelectedNeighborhood('');
      }
    }
  }, [value, hierarchy]);

  // Handle state selection
  const handleStateClick = (state: string) => {
    setSelectedState(state);
    setSelectedCity('');
    setSelectedNeighborhood('');
    
    if (state && hierarchy.states.has(state)) {
      onChange(hierarchy.states.get(state)!.pk);
    } else {
      onChange('');
    }
  };

  // Handle city selection
  const handleCityClick = (city: string) => {
    setSelectedCity(city);
    setSelectedNeighborhood('');
    
    if (city && selectedState && 
        hierarchy.states.has(selectedState) && 
        hierarchy.states.get(selectedState)!.cities.has(city)) {
      onChange(hierarchy.states.get(selectedState)!.cities.get(city)!.pk);
    } else if (selectedState && hierarchy.states.has(selectedState)) {
      onChange(hierarchy.states.get(selectedState)!.pk);
    } else {
      onChange('');
    }
  };

  // Handle neighborhood selection
  const handleNeighborhoodClick = (neighborhood: string) => {
    setSelectedNeighborhood(neighborhood);
    
    if (neighborhood && selectedState && selectedCity &&
        hierarchy.states.has(selectedState) &&
        hierarchy.states.get(selectedState)!.cities.has(selectedCity) &&
        hierarchy.states.get(selectedState)!.cities.get(selectedCity)!.neighborhoods.has(neighborhood)) {
      onChange(hierarchy.states.get(selectedState)!.cities.get(selectedCity)!.neighborhoods.get(neighborhood)!.pk);
    } else if (selectedState && selectedCity &&
               hierarchy.states.has(selectedState) &&
               hierarchy.states.get(selectedState)!.cities.has(selectedCity)) {
      onChange(hierarchy.states.get(selectedState)!.cities.get(selectedCity)!.pk);
    } else if (selectedState && hierarchy.states.has(selectedState)) {
      onChange(hierarchy.states.get(selectedState)!.pk);
    } else {
      onChange('');
    }
  };

  if (loading) {
    return <CircularProgress size={24} />;
  }

  // Get sorted and filtered lists
  const states = Array.from(hierarchy.states.keys())
    .filter(state => state.toLowerCase().includes(stateFilter.toLowerCase()))
    .sort();
  
  const cities = selectedState && hierarchy.states.has(selectedState)
    ? Array.from(hierarchy.states.get(selectedState)!.cities.keys())
      .filter(city => city.toLowerCase().includes(cityFilter.toLowerCase()))
      .sort()
    : [];
    
  const neighborhoods = selectedState && selectedCity &&
    hierarchy.states.has(selectedState) &&
    hierarchy.states.get(selectedState)!.cities.has(selectedCity)
    ? Array.from(hierarchy.states.get(selectedState)!.cities.get(selectedCity)!.neighborhoods.keys())
      .filter(neighborhood => neighborhood.toLowerCase().includes(neighborhoodFilter.toLowerCase()))
      .sort()
    : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%', mb: 1 }}>
        {/* States Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            States
          </Typography>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search states…"
              inputProps={{ 'aria-label': 'search states' }}
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              disabled={disabled}
            />
          </Search>
          <ScrollableList>
            <List disablePadding>
              {states.map((state) => (
                React.createElement(
                  state === selectedState ? SelectedListItem : ListItemButton,
                  {
                    key: state,
                    selected: state === selectedState,
                    onClick: () => handleStateClick(state),
                    disabled: disabled,
                  },
                  <ListItemText primary={state} />
                )
              ))}
              {states.length === 0 && (
                <ListItemButton disabled>
                  <ListItemText primary="No states found" />
                </ListItemButton>
              )}
            </List>
          </ScrollableList>
        </Box>

        {/* Cities Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Cities
          </Typography>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search cities…"
              inputProps={{ 'aria-label': 'search cities' }}
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              disabled={disabled || !selectedState}
            />
          </Search>
          <ScrollableList>
            <List disablePadding>
              {cities.map((city) => (
                React.createElement(
                  city === selectedCity ? SelectedListItem : ListItemButton,
                  {
                    key: city,
                    selected: city === selectedCity,
                    onClick: () => handleCityClick(city),
                    disabled: disabled,
                  },
                  <ListItemText primary={city} />
                )
              ))}
              {selectedState && cities.length === 0 && (
                <ListItemButton disabled>
                  <ListItemText primary="No cities found" />
                </ListItemButton>
              )}
              {!selectedState && (
                <ListItemButton disabled>
                  <ListItemText primary="Select a state first" />
                </ListItemButton>
              )}
            </List>
          </ScrollableList>
        </Box>

        {/* Neighborhoods Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Neighborhoods
          </Typography>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search neighborhoods…"
              inputProps={{ 'aria-label': 'search neighborhoods' }}
              value={neighborhoodFilter}
              onChange={(e) => setNeighborhoodFilter(e.target.value)}
              disabled={disabled || !selectedCity}
            />
          </Search>
          <ScrollableList>
            <List disablePadding>
              {neighborhoods.map((neighborhood) => (
                React.createElement(
                  neighborhood === selectedNeighborhood ? SelectedListItem : ListItemButton,
                  {
                    key: neighborhood,
                    selected: neighborhood === selectedNeighborhood,
                    onClick: () => handleNeighborhoodClick(neighborhood),
                    disabled: disabled,
                  },
                  <ListItemText primary={neighborhood} />
                )
              ))}
              {selectedCity && neighborhoods.length === 0 && (
                <ListItemButton disabled>
                  <ListItemText primary="No neighborhoods found" />
                </ListItemButton>
              )}
              {!selectedCity && (
                <ListItemButton disabled>
                  <ListItemText primary="Select a city first" />
                </ListItemButton>
              )}
            </List>
          </ScrollableList>
        </Box>
      </Box>
      
      {error && <FormHelperText error>{error}</FormHelperText>}
      <Box sx={{ mt: 1 }}>
        <FormHelperText>
          {value ? `Selected: ${value}` : 'This links the publisher to a specific location in the system'}
        </FormHelperText>
      </Box>
    </Box>
  );
};

export default LocationSelector;